import React from 'react';
import { useTranslation } from 'react-i18next';

import TableCell from '@mui/material/TableCell';

import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { ApolloError } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import Moment from '../../../../components/Moment';
import { ICompanySubdirectorySubscriptionSchema } from '../../../../../graphql/type/companySubdirectorySubscription';
import CopyIconButton from '../../../../components/_iconButton/CopyIconButton';
import { IProductSchema } from '../../../../../graphql/type/product';
import { useUpdateProductSubdirectoryTagsMutation } from '../../../../../graphql/mutation/product/productSubdirectory/updateProductSubdirectoryTagsMutation';
import useNotification from '../../../../components/_hook/useNotification';
import ButtonSubmit from '../../../../components/_button/ButtonSubmit';

interface IProps {
  item: ICompanySubdirectorySubscriptionSchema;
  product: IProductSchema;
  setTableRefreshKey?: (tableRefreshKey: number) => void;
}

export default function ProductSubdirectoryTableItem(props: IProps) {
  const { t } = useTranslation();
  const { item, setTableRefreshKey, product } = props;
  const { notification } = useNotification();

  function onCompleted() {
    notification.success(t('success'));
  }

  function onError(e: ApolloError) {
    notification.error(e.message);
  }

  const [
    updateProductSubdirectoryTagsMutation,
    { loading: isUpdatingProductSubdirectoryTags },
  ] = useUpdateProductSubdirectoryTagsMutation(
    `updatedProduct {
                _id
                subdirectory {
                  tags {
                    status
                    id
                  }
                }
			}`,
    {
      onCompleted,
      onError,
    }
  );

  const productSubdirectoryTags =
    product.subdirectory && product.subdirectory.tags
      ? product.subdirectory.tags
      : [];

  const subdirectoryTagData =
    item.subdirectoryData && item.subdirectoryData.subdirectoryTagData
      ? item.subdirectoryData.subdirectoryTagData
      : null;
  const productSubdirectoryTag = productSubdirectoryTags.find(
    (tempProductSubdirectoryTag) =>
      item.subdirectoryData &&
      item.subdirectoryData.subdirectoryTagData &&
      tempProductSubdirectoryTag.id ===
        item.subdirectoryData.subdirectoryTagData._id
  );

  if (!subdirectoryTagData) return null;

  return (
    <>
      <TableCell align="left">
        <Tooltip title={item._id}>
          <span>{item._id.substr(0, 6)}</span>
        </Tooltip>
        <CopyIconButton iconHtmlFontSize="0.8rem" copyText={item._id} />
      </TableCell>
      <TableCell align="center">
        {item.subdirectoryData &&
          item.subdirectoryData.domain &&
          item.subdirectoryData.domain.map((domain) => (
            <React.Fragment key={domain}>
              <Button
                color="primary"
                component="a"
                href={`//${domain}`}
                target="_blank"
              >
                <Typography>{domain}</Typography>
              </Button>
              <br />
            </React.Fragment>
          ))}
      </TableCell>
      <TableCell align="center">
        {item.status ? t('active') : t('inactive')}
      </TableCell>
      <TableCell align="center">
        {item.expiryDate ? <Moment time={item.expiryDate} dateOnly /> : '-'}
      </TableCell>
      <TableCell align="center">
        {productSubdirectoryTag ? (
          <>{productSubdirectoryTag.status ? t('active') : t('inactive')}</>
        ) : (
          '-'
        )}
      </TableCell>
      <TableCell align="right">
        {item.status ? (
          <>
            {productSubdirectoryTag && productSubdirectoryTag.status ? (
              <ButtonSubmit
                variant="contained"
                useButtonDanger
                iconPosition="left"
                onClick={() => {
                  updateProductSubdirectoryTagsMutation({
                    variables: {
                      _id: product._id,
                      subdirectoryTagIds: productSubdirectoryTags
                        .filter(
                          (tempProductSubdirectoryTag) =>
                            tempProductSubdirectoryTag.id !==
                            subdirectoryTagData._id
                        )
                        .map(
                          (tempProductSubdirectoryTag) =>
                            tempProductSubdirectoryTag.id
                        ),
                    },
                  });
                }}
                isSubmitting={isUpdatingProductSubdirectoryTags}
                label={t('remove')}
                icon={<RemoveIcon />}
              />
            ) : (
              <ButtonSubmit
                variant="contained"
                iconPosition="left"
                color="primary"
                onClick={() => {
                  updateProductSubdirectoryTagsMutation({
                    variables: {
                      _id: product._id,
                      subdirectoryTagIds: [
                        ...productSubdirectoryTags.map(
                          (tempProductSubdirectoryTag) =>
                            tempProductSubdirectoryTag.id
                        ),
                        ...[subdirectoryTagData._id],
                      ],
                    },
                  });
                }}
                isSubmitting={isUpdatingProductSubdirectoryTags}
                label={t('add')}
                icon={<AddIcon />}
              />
            )}
          </>
        ) : (
          t('disable_due_to_subscription_inactive')
        )}
      </TableCell>
    </>
  );
}
